/**
 * EmailGate Component
 * 
 * Authentication gate that collects email addresses and sends magic links.
 * Blocks unauthenticated users from accessing the application.
 */

import React, { useState, FormEvent, KeyboardEvent, useRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { useAuth } from '../hooks';
import { useTranslation } from '../hooks/useTranslation';
import { ErrorDisplay } from './ErrorDisplay';
import { LanguageButton } from './LanguageButton';
import { GlobeBackground } from './GlobeBackground';
import type { MapLanguage } from '../types';
import styles from './EmailGate.module.css';

/**
 * EmailGate component props
 */
export interface EmailGateProps {
  /** Current language */
  language: MapLanguage;
  /** Callback when language changes */
  onLanguageChange: (language: MapLanguage) => void;
}

/**
 * EmailGate Component
 * 
 * Displays email input form with validation and magic link flow.
 * Shows different UI states: input, sending, success, error.
 */
export const EmailGate: React.FC<EmailGateProps> = ({ 
  language,
  onLanguageChange,
}) => {
  const { t } = useTranslation();
  const { signIn, signInWithTestCode } = useAuth();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCardVisible, setIsCardVisible] = useState(false);
  
  // Turnstile CAPTCHA state
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined);
  const captchaRef = useRef<any>(null);
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  
  // Test code flow state
  const [showTestCodeInput, setShowTestCodeInput] = useState(false);
  const [testCode, setTestCode] = useState('');
  const [testCodeError, setTestCodeError] = useState<string | null>(null);
  const [showOptionalEmail, setShowOptionalEmail] = useState(false);
  const [optionalEmail, setOptionalEmail] = useState('');
  const [optionalEmailError, setOptionalEmailError] = useState<string | null>(null);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [validatedCode, setValidatedCode] = useState<string | null>(null);

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) {
      setError(t.emailGate.emailRequired);
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError(t.emailGate.emailInvalid);
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      // Update email state with trimmed value for display in success screen
      setEmail(trimmedEmail);
      await signIn(trimmedEmail, captchaToken);
      setEmailSent(true);
    } catch (err) {
      const rawErrorMessage = err instanceof Error ? err.message : 'TRANSLATE:emailGate.failedToSend';
      const translatedError = translateError(rawErrorMessage);
      setError(translatedError);
      setEmailSent(false);
      // Reset captcha on error
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle Enter key press
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isSubmitting && !emailSent) {
      const form = e.currentTarget.form;
      if (form) {
        form.requestSubmit();
      }
    }
  };

  /**
   * Handle retry after error
   */
  const handleRetry = () => {
    setError(null);
    setEmailSent(false);
  };

  /**
   * Handle Test Me button click - show login card
   */
  const handleTestMeClick = () => {
    setIsCardVisible(true);
  };

  /**
   * Toggle between email and test code input
   */
  const handleTestCodeToggle = () => {
    setShowTestCodeInput(!showTestCodeInput);
    setError(null);
    setTestCodeError(null);
  };

  /**
   * Handle test code submission
   * Only validates the code, doesn't authenticate yet
   */
  const handleTestCodeSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const trimmedCode = testCode.trim();
    
    if (!trimmedCode) {
      setTestCodeError(t.testCode?.invalid || 'Invalid test code');
      return;
    }

    try {
      setIsValidatingCode(true);
      setTestCodeError(null);
      
      // Import validateTestCode dynamically to avoid circular dependency
      const { validateTestCode } = await import('../services/testCodeService');
      const validation = await validateTestCode(trimmedCode);
      
      if (!validation.valid) {
        const rawErrorMessage = validation.errorMessage || 'TRANSLATE:testCode.invalid';
        const translatedError = translateError(rawErrorMessage);
        setTestCodeError(translatedError);
        return;
      }
      
      // Code is valid, save it and show optional email prompt
      setValidatedCode(trimmedCode);
      setShowOptionalEmail(true);
      setShowTestCodeInput(false);
    } catch (err) {
      const rawErrorMessage = err instanceof Error ? err.message : 'TRANSLATE:testCode.invalid';
      const translatedError = translateError(rawErrorMessage);
      setTestCodeError(translatedError);
    } finally {
      setIsValidatingCode(false);
    }
  };

  /**
   * Handle optional email submission or skip
   * Now actually authenticates with the validated code and optional email
   */
  const handleOptionalEmailSubmit = async (skip: boolean = false) => {
    if (!validatedCode) {
      setTestCodeError('Code validation error. Please try again.');
      setShowOptionalEmail(false);
      setShowTestCodeInput(true);
      return;
    }

    // Validate email format if provided
    if (!skip && optionalEmail.trim()) {
      const trimmedEmail = optionalEmail.trim().toLowerCase();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        setOptionalEmailError(t.emailGate?.emailInvalid || 'Invalid email format');
        return;
      }
    }

    try {
      setIsAuthenticating(true);
      setOptionalEmailError(null);
      
      // Authenticate with test code and optional email
      const emailToUse = skip ? undefined : optionalEmail.trim().toLowerCase();
      await signInWithTestCode(validatedCode, emailToUse, captchaToken);
      
      // Authentication successful, close the gate
      setEmailSent(true);
    } catch (err) {
      const rawErrorMessage = err instanceof Error ? err.message : 'TRANSLATE:testCode.invalid';
      const translatedError = translateError(rawErrorMessage);
      setTestCodeError(translatedError);
      // Go back to test code input on error
      setShowOptionalEmail(false);
      setShowTestCodeInput(true);
      // Reset captcha on error
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  /**
   * Safely interpolate email into message string
   * @param message - Message template with {email} placeholder
   * @param email - Email to insert
   * @returns Interpolated message or original if no placeholder found
   */
  const interpolateEmail = (message: string, email: string): React.ReactNode => {
    const parts = message.split('{email}');
    if (parts.length === 1) {
      // No placeholder found, return as is
      return message;
    }
    // Interpolate with email wrapped in <strong>
    return (
      <>
        {parts[0]}
        <strong>{email}</strong>
        {parts[1] || ''}
      </>
    );
  };

  /**
   * Translate error message from authService
   * Parses TRANSLATE: prefix and interpolates parameters
   * @param errorMessage - Error message from authService
   * @returns Translated error message
   */
  const translateError = (errorMessage: string): string => {
    if (!errorMessage.startsWith('TRANSLATE:')) {
      // Not a translation key, return as-is (fallback)
      return errorMessage;
    }

    // Parse format: "TRANSLATE:key" or "TRANSLATE:key:param" or "TRANSLATE:key:SUGGESTION:suggestionKey:param"
    const parts = errorMessage.substring(10).split(':');
    const mainKey = parts[0];
    
    // Get main translation
    const mainTranslation = mainKey.split('.').reduce((obj: any, key: string) => obj?.[key], t) as string || errorMessage;
    
    // Check for suggestion
    if (parts[1] === 'SUGGESTION' && parts[2] && parts[3]) {
      const suggestionKey = parts[2];
      const suggestionParam = parts[3];
      const suggestionTranslation = suggestionKey.split('.').reduce((obj: any, key: string) => obj?.[key], t) as string || '';
      
      // Interpolate {domain} in suggestion
      const interpolatedSuggestion = suggestionTranslation.replace('{domain}', suggestionParam);
      
      return `${mainTranslation}. ${interpolatedSuggestion}`;
    }
    
    // Check for single parameter (like error message)
    if (parts[1]) {
      return mainTranslation.replace('{error}', parts[1]);
    }
    
    return mainTranslation;
  };

  // Email Sent State - always show card
  if (emailSent) {
    return (
      <div className={styles.overlay}>
        <GlobeBackground language={language} />
        <div className={styles.languageSelector}>
          <LanguageButton language={language} onLanguageChange={onLanguageChange} />
        </div>
        <div className={`${styles.card} ${styles.cardVisible}`}>
          <div className={styles.successIcon}>✓</div>
          <h2 className={styles.title}>{t.emailGate.checkEmailTitle}</h2>
          <p className={styles.message}>
            {interpolateEmail(t.emailGate.checkEmailMessage, email)}
          </p>
          <p className={styles.instruction}>
            {t.emailGate.checkEmailInstruction}
          </p>
          <p className={styles.helpText}>
            {t.emailGate.checkEmailHelp}
          </p>
        </div>
      </div>
    );
  }

  // Error State - always show card
  if (error) {
    return (
      <div className={styles.overlay}>
        <GlobeBackground language={language} />
        <div className={styles.languageSelector}>
          <LanguageButton language={language} onLanguageChange={onLanguageChange} />
        </div>
        <div className={`${styles.card} ${styles.cardVisible}`}>
          <h2 className={styles.title}>{t.emailGate.title}</h2>
          <p className={styles.subtitle}>
            {t.emailGate.subtitle}
          </p>
          <ErrorDisplay message={error} onRetry={handleRetry} />
          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t.emailGate.emailPlaceholder}
              className={styles.emailInput}
              disabled={isSubmitting}
              autoFocus
              aria-label={t.emailGate.emailLabel}
              aria-invalid={!!error}
              aria-describedby={error ? 'email-error' : undefined}
            />
            {turnstileSiteKey && (
              <div className={styles.captchaContainer}>
                <Turnstile
                  ref={captchaRef}
                  siteKey={turnstileSiteKey}
                  onSuccess={(token) => setCaptchaToken(token)}
                  onError={() => setCaptchaToken(undefined)}
                  onExpire={() => setCaptchaToken(undefined)}
                  options={{
                    theme: 'auto',
                    size: 'normal',
                  }}
                />
              </div>
            )}
            <button
              type="submit"
              disabled={isSubmitting || !email.trim() || (turnstileSiteKey && !captchaToken)}
              className={styles.submitButton}
            >
              {isSubmitting ? (
                <>
                  <span className={styles.spinner}></span>
                  {t.emailGate.sendingButton}
                </>
              ) : (
                t.emailGate.sendButton
              )}
            </button>
          </form>
          <div className={styles.privacyNote}>
            <small>
              {t.emailGate.domainRestriction}
            </small>
          </div>
        </div>
      </div>
    );
  }

  // Input State / Sending State
  return (
    <div className={styles.overlay}>
      <GlobeBackground language={language} />
      <div className={styles.languageSelector}>
        <LanguageButton language={language} onLanguageChange={onLanguageChange} />
      </div>
      
      {/* Banner - shown when card is hidden */}
      {!isCardVisible && (
        <div className={styles.banner}>
          <span className={styles.bannerText}>{t.emailGate.bannerText}</span>
          <span className={styles.betaTag}>{t.emailGate.betaTag}</span>
        </div>
      )}

      {/* Test Me Button - shown when card is hidden */}
      {!isCardVisible && (
        <button 
          className={styles.testMeButton}
          onClick={handleTestMeClick}
          aria-label={t.emailGate.testMeButton}
        >
          <span className={styles.testMeIcon}>🌍</span>
          <span className={styles.testMeText}>{t.emailGate.testMeButton}</span>
        </button>
      )}

      {/* Login Card - slides up from bottom */}
      <div className={`${styles.card} ${isCardVisible ? styles.cardVisible : ''}`}>
        {showOptionalEmail ? (
          // Optional email prompt after successful test code validation
          <>
            <h2 className={styles.title}>{t.testCode?.optionalEmailTitle || 'Save Your Progress (Optional)'}</h2>
            <p className={styles.subtitle}>
              {t.testCode?.optionalEmailSubtitle || 'Enter your email to save your calculations'}
            </p>
            {optionalEmailError && (
              <div style={{ color: '#ef4444', marginBottom: '1rem', textAlign: 'center' }}>
                {optionalEmailError}
              </div>
            )}
            <div className={styles.form}>
              <input
                type="email"
                value={optionalEmail}
                onChange={(e) => {
                  setOptionalEmail(e.target.value);
                  setOptionalEmailError(null);
                }}
                placeholder={t.testCode?.optionalEmailPlaceholder || 'your.email@example.com (optional)'}
                className={styles.emailInput}
                disabled={isAuthenticating}
                autoFocus
                aria-label="Optional email"
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => handleOptionalEmailSubmit(true)}
                  className={styles.submitButton}
                  style={{ flex: 1 }}
                  disabled={isAuthenticating}
                >
                  {isAuthenticating ? (
                    <>
                      <span className={styles.spinner}></span>
                      Signing in...
                    </>
                  ) : (
                    t.testCode?.skipButton || 'Skip for now'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleOptionalEmailSubmit(false)}
                  className={styles.submitButton}
                  style={{ flex: 1 }}
                  disabled={!optionalEmail.trim() || isAuthenticating}
                >
                  {isAuthenticating ? (
                    <>
                      <span className={styles.spinner}></span>
                      Signing in...
                    </>
                  ) : (
                    t.testCode?.submitEmailButton || 'Continue'
                  )}
                </button>
              </div>
            </div>
          </>
        ) : showTestCodeInput ? (
          // Test code input
          <>
            <h2 className={styles.title}>{t.testCode?.title || 'Enter Test Code'}</h2>
            <p className={styles.subtitle}>
              {t.testCode?.subtitle || 'Have a test code? Enter it below'}
            </p>
            {testCodeError && (
              <div style={{ color: '#ef4444', marginBottom: '1rem', textAlign: 'center' }}>
                {testCodeError}
              </div>
            )}
            <form onSubmit={handleTestCodeSubmit} className={styles.form} noValidate>
              <input
                type="text"
                value={testCode}
                onChange={(e) => setTestCode(e.target.value.toUpperCase())}
                placeholder={t.testCode?.placeholder || 'Enter code'}
                className={styles.testCodeInput}
                disabled={isValidatingCode}
                autoFocus={isCardVisible}
                aria-label="Test code"
              />
              <button
                type="submit"
                disabled={isValidatingCode || !testCode.trim()}
                className={styles.submitButton}
              >
                {isValidatingCode ? (
                  <>
                    <span className={styles.spinner}></span>
                    Validating...
                  </>
                ) : (
                  t.testCode?.button || 'Continue with Code'
                )}
              </button>
            </form>
            <button
              type="button"
              onClick={handleTestCodeToggle}
              className={styles.privacyNote}
              style={{ cursor: 'pointer', background: 'none', border: 'none', padding: '0.5rem' }}
            >
              <small style={{ textDecoration: 'underline' }}>
                {t.testCode?.useEmailLink || 'Use email instead'}
              </small>
            </button>
          </>
        ) : (
          // Email input (default)
          <>
            <h2 className={styles.title}>{t.emailGate.title}</h2>
            <p className={styles.subtitle}>
              {t.emailGate.subtitle}
            </p>
            <form onSubmit={handleSubmit} className={styles.form} noValidate>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t.emailGate.emailPlaceholder}
                className={styles.emailInput}
                disabled={isSubmitting}
                autoFocus={isCardVisible}
                aria-label={t.emailGate.emailLabel}
              />
              {turnstileSiteKey && (
                <div className={styles.captchaContainer}>
                  <Turnstile
                    ref={captchaRef}
                    siteKey={turnstileSiteKey}
                    onSuccess={(token) => setCaptchaToken(token)}
                    onError={() => setCaptchaToken(undefined)}
                    onExpire={() => setCaptchaToken(undefined)}
                    options={{
                      theme: 'auto',
                      size: 'normal',
                    }}
                  />
                </div>
              )}
              <button
                type="submit"
                disabled={isSubmitting || !email.trim() || (turnstileSiteKey && !captchaToken)}
                className={styles.submitButton}
              >
                {isSubmitting ? (
                  <>
                    <span className={styles.spinner}></span>
                    {t.emailGate.sendingButton}
                  </>
                ) : (
                  t.emailGate.sendButton
                )}
              </button>
            </form>
            <div className={styles.privacyNote}>
              <small>
                {t.emailGate.domainRestriction}
              </small>
            </div>
            <button
              type="button"
              onClick={handleTestCodeToggle}
              className={styles.privacyNote}
              style={{ cursor: 'pointer', background: 'none', border: 'none', padding: '0.5rem', marginTop: '0.5rem' }}
            >
              <small style={{ textDecoration: 'underline' }}>
                {t.testCode?.haveCodeLink || 'Have a test code?'}
              </small>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

